import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';
import { UserProfilePermission } from 'app/shared/models/profiles-permissions/userProfilePermission.model';
import { Store } from '@ngrx/store';
import { UserStore } from 'app/core/user/user.types';
import { defaultNavigation } from 'app/mock-api/common/navigation/data';

function buildNavLinkMap(items: any[], acc = new Map<string, string>()): Map<string, string> {
  for (const item of items) {
    if (item.id && item.link) acc.set(item.id.trim().toLowerCase(), item.link);
    if (item.children?.length) buildNavLinkMap(item.children, acc);
  }
  return acc;
}
const NAV_LINK_MAP = buildNavLinkMap(defaultNavigation);

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad {
  /**
   * Constructor
   */
  constructor(
    private _authService: AuthService,
    private _router: Router,
    private store: Store
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this._check(redirectUrl);
    return true;
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this._check(redirectUrl);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    const redirectUrl = route.path === 'sign-out' ? '/' : route.path;
    return this._check(redirectUrl);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @param redirectURL
   * @private
   */
  private _check(redirectURL: string): Observable<boolean> {
    return this._authService.check().pipe(
      switchMap(authenticated => {
        if (!authenticated) {
          // Redirection si l'utilisateur n'est pas authentifié
          this._router.navigate(['sign-in'], { queryParams: { redirectURL } });
          return of(false);
        }
        // Si l'utilisateur est authentifié, attendre l'utilisateur connecté depuis le store
        return this.store
          .select((s: any) => (s['user']?.connectedUser ?? s['user']?.user) as UserStore | undefined)
          .pipe(
            filter((u): u is UserStore => !!u),
            take(1),
            map(connectedUser => {
              // Always allow access to forbidden-access page
              if (redirectURL.includes('forbidden-access')) {
                return true;
              }
              // Super admin fast-path
              if (connectedUser?.email === 'superadmin@aceenergie.com') {
                return true;
              }
              const permissions: UserProfilePermission[] = connectedUser.permissions || [];
              if (permissions.length === 0) {
                this._router.navigateByUrl('/forbidden-access');
                return false;
              }
              // Normaliser l'URL de redirection (retirer query/hash, normaliser case et slashes)
              const strip = (v: string) => v.toLowerCase().split('#')[0].split('?')[0].replace(/\/$/, '');
              let normalized = strip(redirectURL);
              // retirer leading slash pour comparaison tolérante
              normalized = normalized.replace(/^\//, '');
              // ignorer un dernier segment numérique (ex: id) si présent
              const parts = normalized.split('/');
              if (parts.length > 0 && /^\d+$/.test(parts[parts.length - 1])) {
                parts.pop();
                normalized = parts.join('/');
              }
              // Vérifier si une permission correspond (préfixe ou inclusion dans les deux sens)
              const hasPermission = permissions.some(permission => {
                const appComponentId = permission.teraComponent?.appComponentId;
                if (!appComponentId) return false;
                let link = NAV_LINK_MAP.get(appComponentId.trim().toLowerCase()) || '';
                if (!link) return false;
                link = strip(link).replace(/^\//, '');
                if (!link) return false;
                return (
                  normalized === link ||
                  normalized.startsWith(link + '/') ||
                  link.startsWith(normalized + '/') ||
                  normalized.includes(link) ||
                  link.includes(normalized)
                );
              });
              if (hasPermission) {
                return true;
              }
              this._router.navigateByUrl('/forbidden-access');
              return false;
            })
          );
      })
    );
  }
}
